// libraries
import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

// components
import {
  Input,
  INPUT_THEME,
  Form,
  CircularLoader,
  Pagination,
} from '@st-operations/uikit/src/v2/index.js'

import BatchProcessModal from '_components/batch-process-modal'
import Table, { CHECKBOX_ACTION } from '_components/table'

// libraries
import getQueryParam from '_root/lib/getQueryParam.js'

// services
import Events from '_root/Events.js'
import Storage from '_root/Storage.js'
import Team from '_root/Team.js'

// css
import './styles.scss'

// the backend caps list page size (~250); stay at/under it and page through
const FETCH_ALL_PAGE_SIZE = 250
// how many pages to request in parallel while gathering an "all pages" selection
const FETCH_ALL_CONCURRENCY = 6

const BATCH_MODAL_TEXTS = {
  TITLE: {
    Delete: 'Delete Audiences - Batch Progress',
    Clone: 'Clone Audiences - Batch Progress',
    Archive: 'Archive Audiences - Batch Progress',
    Approve: 'Approve Audiences - Batch Progress',
    Default: 'Batch Progress',
  },
  BUTTON: {
    Delete: 'Cancel Delete',
    Clone: 'Cancel Clone',
    Archive: 'Cancel Archive',
    Approve: 'Cancel Approve',
    Default: 'Cancel',
  },
}

// component
class ListView extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      error: null,
      filters: {},
      page: 1,
      page_size: props.page_size || 25,
      query: '',
      selected_ids: new Set(),
      sort_dir: props.sort_dir,
      sort_key: props.sort_key,
      team: null,
      optionSelected: '',
      all_pages_selected: false,
      all_pages_loading: false,
      total_count: 0,
    }
    this.table = React.createRef()
    this.searchbox = React.createRef()
    // bumped whenever the query/filters/sort change; an "all pages" harvest
    // captures the current value and discards its result if it no longer matches
    this.harvestToken = 0
  }

  componentDidMount() {
    const { filter_on_load, persistentFiltersOnRefresh, entities, stateful } = this.props
    const storedFilters = localStorage.getItem(`@sharethis/${entities}/filters`)

    //instruct backend and filters UI about special fields
    const fieldsQueryObj = this.props.q?.split('&').reduce((obj, keyValue) => {
      const [key, value] = keyValue.split('=')
      return { ...obj, [key]: value }
    }, {})
    this.setState({ fieldsQueryObj, filters: fieldsQueryObj })

    this.mounted = true

    Team.watch(this, (team) => {
      this.setState({ team })
    })

    // check querystring for query and page
    let page = parseInt(getQueryParam('p'), 10)
    // eslint-disable-next-line eqeqeq
    let query = query != null ? decodeURI(getQueryParam('q')) : ''

    if (page || query) {
      this.setState({ page, query }, this.search)
    }

    // if filter on load set to true preserve filters
    else if (filter_on_load) {
      const isSearched = Storage.has(`ListView.${entities}.state`)
      if (stateful && isSearched) {
        const { page: searchPage, query: searchQuery } = Storage.get(`ListView.${entities}.state`)
        if (searchPage || searchQuery) {
          this.setState(
            { filters: filter_on_load, page: searchPage, query: searchQuery },
            this.search
          )
        }
      } else {
        this.setState({ filters: filter_on_load, page: 1 }, this.search)
      }
    }

    // reload previous query and page
    else if (Storage.has(`ListView.${entities}.state`)) {
      let { entities, page, query } = Storage.get(`ListView.${entities}.state`)
      if (entities === this.props.entities) {
        this.setState({ page, query }, this.search)
      } else {
        this.search()
      }
    }

    // check querystring for query and page
    else {
      this.search()
    }

    // focus on search box
    if (this.searchbox.current) {
      this.searchbox.current.focus()
    }

    Events.addListener('set_batch_process', this.setBatchProcess)

    if (persistentFiltersOnRefresh && storedFilters && !this.props.dontStoreFilters) {
      this.setState({ filters: JSON.parse(storedFilters), page: 1 }, this.search)
      return
    }
  }

  componentWillUnmount() {
    this.mounted = false
  }

  componentDidUpdate() {
    if (this.props.itemsRef) {
      this.props.itemsRef.current = this.state.items
    }
  }

  setBatchProcess = (open) => {
    this.setState({ batch_process_open: open })
  }

  // clearing the current result set (search/filter/sort) invalidates an
  // "all pages" selection, so drop it and clear the sidebar
  resetAllPagesSelection = () => {
    // any harvest still in flight is now stale — force its result to be discarded
    this.harvestToken++

    if (!this.state.all_pages_selected && !this.state.all_pages_loading) return

    // untick the checkboxes we own. The sidebar's clear handler can't do it —
    // it captured an empty item list at mount — so emit the delete ourselves
    // with the ids we're currently holding.
    if (this.state.selected_ids.size > 0) {
      Events.emit('Table-handle-selected', {
        action: CHECKBOX_ACTION.DELETE,
        ids: [...this.state.selected_ids],
      })
    }
    this.setState({
      selected_ids: new Set(),
      all_pages_selected: false,
      all_pages_loading: false,
    })
    Events.emit('ListView-selection--clear')
  }

  onSearch = (value) => {
    let query = value
    this.resetAllPagesSelection()
    this.setState({ query })

    // debounce
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
    this.timeout = setTimeout(() => {
      let page = 1
      this.setState({ page, query }, this.search)
    }, 500)
  }

  onFilter = (value, key) => {
    let { filters } = this.state
    const { entities, persistentFiltersOnRefresh } = this.props
    this.resetAllPagesSelection()

    if (value) {
      filters[key] = value
    } else {
      delete filters[key]
    }

    if (persistentFiltersOnRefresh) {
      localStorage.setItem(`@sharethis/${entities}/filters`, JSON.stringify(filters))
    }

    // Go to first page before filtering
    this.setState({ filters, page: 1 }, this.search)
  }

  onSort = (key) => {
    let { sort_dir } = this.state
    this.resetAllPagesSelection()

    if (key === this.state.sort_key) {
      this.setState({ sort_dir: sort_dir * -1 }, this.search)
    } else {
      this.setState({ sort_key: key, sort_dir: 1 }, this.search)
    }
  }

  // build the querystring for a list request from the current query/filters/sort.
  // shared by search() (a single page) and fetchAllIds() (every page) so the two
  // constructions can't drift apart.
  buildQs = ({ limit, skip, fields }) => {
    const { qs: queryString } = this.props
    const { query, filters, sort_key, sort_dir } = this.state
    const qs = { ...queryString }
    for (const key in filters) {
      if (filters[key]?._id) {
        qs[`${key}._id`] = filters[key]._id
      } else if (filters[key]?.value) {
        qs[key] = filters[key].value
      } else {
        qs[key] = filters[key]
      }
    }
    qs.fields = fields
    qs.limit = limit
    qs.q = (query || '').trim().toLowerCase()
    qs.skip = skip
    Object.assign(qs, this.state.fieldsQueryObj || {})
    if (sort_key && sort_dir) {
      qs.sort = `${sort_key},${sort_dir}`
    }
    return qs
  }

  search = () => {
    let { data, entities, fields, search, qs: queryString, onGetItems } = this.props
    let { page, page_size } = this.state
    let { filters, sort_key, sort_dir } = this.state
    let qs = { ...queryString }
    this.rememberState()
    this.updateQueryString()
    this.setState(
      {
        is_loading: true,
      },
      () => {
        if (data) {
          if (search) {
            search(sort_dir, sort_key, filters, (items) => {
              let start = (page - 1) * page_size
              this.setState({
                items: items.slice(start, start + page_size),
                is_loading: false,
                num_pages: parseInt(items.length / page_size, 10) + 1,
                total_count: items.length,
              })
            })
          } else {
            let start = (page - 1) * page_size
            this.setState({
              items: data.slice(start, start + page_size),
              is_loading: false,
              num_pages: parseInt(data.length / page_size, 10) + 1,
              total_count: data.length,
            })
          }
        } else if (entities) {
          qs = this.buildQs({
            limit: page_size,
            skip: ((page || 1) - 1) * page_size,
            fields,
          })
          if (!search) {
            search = (qs, next) => {
              window._api.get(`/${entities}`, qs, next)
            }
          }
          search(qs, (err, resp) => {
            if (this.mounted) {
              if (err) {
                this.setState({
                  error: err,
                  is_loading: false,
                })
              } else {
                this.setState({
                  items: this.props.extractItems(resp),
                  is_loading: false,
                  num_pages: parseInt(this.props.extractCount(resp) / page_size, 10) + 1,
                  total_count: this.props.extractCount(resp),
                })
                //Checking for null object
                if (onGetItems) {
                  onGetItems(this.props.extractItems(resp))
                }
              }
            }
          })
        }
      }
    )
  }

  handlePageChange = (page) => {
    this.setState({ page: page?.label ?? 1 }, this.search)
  }

  onCheckboxClick = (tableState) => {
    const update = { selected_ids: new Set(tableState.currentSelected) }
    // deselecting anything means we're no longer in "all pages" mode
    if (tableState.action === CHECKBOX_ACTION.DELETE) {
      update.all_pages_selected = false
    }
    this.setState(update)
    this.props.onSelectionChange(tableState)
  }

  // fetch the ids of every record matching the current query/filters,
  // ignoring pagination, so a bulk action can be applied to all of them.
  // the backend caps page size (~250), so we page through with skip and run
  // the pages in parallel waves to keep it fast for large taxonomies.
  fetchAllIds = async (cb) => {
    const { entities, search: customSearch } = this.props

    if (!entities) {
      cb((this.props.data || []).map(({ _id }) => _id))
      return
    }

    const runner = customSearch || ((qs, next) => window._api.get(`/${entities}`, qs, next))
    const fetchPage = (skip) =>
      new Promise((resolve, reject) => {
        runner(this.buildQs({ limit: FETCH_ALL_PAGE_SIZE, skip, fields: ['_id'] }), (err, resp) => {
          if (err) {
            reject(err)
            return
          }
          resolve({
            ids: this.props.extractItems(resp).map(({ _id }) => _id),
            count: this.props.extractCount(resp),
          })
        })
      })

    try {
      const first = await fetchPage(0)
      const total = first.count || first.ids.length
      const ids = [...first.ids]

      const offsets = []
      for (let skip = FETCH_ALL_PAGE_SIZE; skip < total; skip += FETCH_ALL_PAGE_SIZE) {
        offsets.push(skip)
      }

      for (let i = 0; i < offsets.length; i += FETCH_ALL_CONCURRENCY) {
        const wave = offsets.slice(i, i + FETCH_ALL_CONCURRENCY)
        const pages = await Promise.all(wave.map(fetchPage))
        pages.forEach((page) => ids.push(...page.ids))
      }

      cb(ids)
    } catch (err) {
      if (this.mounted) {
        this.setState({ error: err })
      }
      cb(null)
    }
  }

  handleSelectAllPages = (checked) => {
    if (checked) {
      if (this.state.all_pages_loading) return
      // snapshot the request token; if the query/filters/sort change (or we
      // unmount) before the harvest lands, its result is stale and dropped
      const token = this.harvestToken
      this.setState({ all_pages_loading: true })
      this.fetchAllIds((ids) => {
        if (!this.mounted || token !== this.harvestToken) return
        // a failed fetch returns null; drop back to the current-page selection
        if (!ids) {
          this.setState({ all_pages_loading: false, all_pages_selected: false })
          return
        }
        // check every row across all pages and hand the full id set to the sidebar
        Events.emit('Table-handle-selected', { action: CHECKBOX_ACTION.ADD, ids })
        this.setState({
          all_pages_selected: true,
          all_pages_loading: false,
          selected_ids: new Set(ids),
        })
        this.props.onSelectionChange({
          action: CHECKBOX_ACTION.SELECT_ALL_PAGES,
          items: ids,
          entities: ids.map((_id) => ({ _id })),
          allPages: true,
          count: ids.length,
        })
      })
      return
    }

    // revert to just the current page's selection
    const currentItems = this.state.items || []
    const currentIds = currentItems.map(({ _id }) => _id)
    const staleIds = [...this.state.selected_ids].filter((id) => !currentIds.includes(id))
    if (staleIds.length) {
      Events.emit('Table-handle-selected', { action: CHECKBOX_ACTION.DELETE, ids: staleIds })
    }
    this.setState({ all_pages_selected: false, selected_ids: new Set(currentIds) })
    this.props.onSelectionChange({
      action: CHECKBOX_ACTION.SELECT_ALL_PAGES,
      items: currentIds,
      entities: currentItems,
      allPages: false,
      count: currentIds.length,
    })
  }

  rememberState = () => {
    if (this.props.stateful) {
      let { entities } = this.props
      let { query, page } = this.state
      Storage.set(`ListView.${entities}.state`, { entities, query, page })
    }
  }

  updateQueryString = () => {
    if (this.props.stateful) {
      let { query, page } = this.state
      let url = ''
      let params = []
      if (query) {
        params.push(`q=${query}`)
      }
      if (page && page != 1) {
        params.push(`p=${page}`)
      }
      if (params.length > 0) {
        url += `?${params.join('&')}`
      }
      window.history.pushState(null, null, url)
    }
  }

  handleCancelBatchProcess = () => {
    Storage.set('abort_batch', true)
    this.setBatchProcess(false)
    this.closeBatchProgress
  }

  handleCloseBatchProcess = () => {
    this.setBatchProcess(false)
    // Set.clear() returns undefined, so hand back a fresh empty set; also drop
    // out of "all pages" mode now the batch is done
    this.setState({ selected_ids: new Set(), all_pages_selected: false })
  }

  render() {
    const {
      actions,
      showCheckboxes,
      batch_percentage,
      className,
      columns,
      customPlaceholder,
      entities,
      filters,
      style,
      searchable,
      title,
      inputClassName,
      titleClassName,
      hideLabel,
      allowSelectAllPages,
    } = this.props

    const {
      batch_process_open,
      items,
      is_loading,
      query,
      num_pages = 1,
      page,
      optionSelected,
      all_pages_selected,
      all_pages_loading,
    } = this.state

    const options = new Array(num_pages)
      .fill(null)
      .map((_, index) => ({ label: index + 1, value: index + 1 }))

    const placeHolder = customPlaceholder ? customPlaceholder : `Search for ${entities}`

    return (
      <div className={`st-list-view ${className || ''}`} style={style}>
        <div className="st-list-view__header--container">
          <If condition={title}>
            <h2 className={`st-list-view__title ${titleClassName}`}>{title}</h2>
          </If>
          <If condition={searchable}>
            <Input
              ref={this.searchbox}
              className={classnames(inputClassName, 'st-list-view__search')}
              onChange={this.onSearch}
              placeholder={placeHolder}
              theme={INPUT_THEME.SEARCH}
              value={query}
            />
          </If>
          <If condition={filters}>
            <Form
              className={classnames(filters.className, 'st-list-view__filters')}
              fields={filters}
              item={this.state.filters}
              onChange={this.onFilter}
              hideLabel={hideLabel}
            />
          </If>
        </div>

        <div className="st-list-view__table-container">
          <Table
            columns={columns}
            data={items}
            selection={this.props.selection}
            has_checkboxes={actions.length > 0 || showCheckboxes}
            onCheckboxClick={this.onCheckboxClick}
            onSort={this.onSort}
            ref={this.table}
            sort_dir={this.state.sort_dir}
            sort_key={this.state.sort_key}
            showSelectAllPages={
              allowSelectAllPages &&
              // only entities-backed lists are supported: fetchAllIds() harvests
              // ids through the entities endpoint, which honours the active
              // filters. Client-side (data) lists would hand back every row in
              // props.data, ignoring the custom search/filter, so keep the
              // "select all pages" control hidden for them until that path exists.
              Boolean(entities) &&
              this.state.total_count > this.state.page_size
            }
            allPagesSelected={all_pages_selected}
            allPagesLoading={all_pages_loading}
            onSelectAllPages={this.handleSelectAllPages}
            actions={actions.map((action) => ({
              ...action,
              onClick: () => {
                // Remove select all checkbox id from selected_ids
                const { selected_ids } = this.state
                selected_ids.delete('checkbox-select-all')
                action.onClick(selected_ids)
                setTimeout(this.search, 3000)
                this.setState({
                  optionSelected: action.name,
                })
              },
            }))}
            onRowClick={this.props.onRowClick}
          />

          <If condition={is_loading}>
            <div className="loader__container">
              <CircularLoader />
            </div>
          </If>
        </div>

        {!is_loading && this.renderPagination('bottom-pagination', options, page)}

        <If condition={batch_percentage !== undefined}>
          <BatchProcessModal
            title={BATCH_MODAL_TEXTS.TITLE[optionSelected || 'Default']}
            subtitle="This process will be done in a few seconds"
            buttonLabel={BATCH_MODAL_TEXTS.BUTTON[optionSelected || 'Default']}
            isOpen={batch_process_open}
            onClose={this.handleCloseBatchProcess}
            onClick={this.handleCancelBatchProcess}
            percentage={Math.round(batch_percentage * 100) / 100}
          />
        </If>
      </div>
    )
  }

  renderPagination(className, options, page) {
    return (
      options.length > 1 && (
        <Pagination
          options={options}
          page={page - 1}
          onPageChange={this.handlePageChange}
          className={className}
        />
      )
    )
  }
}

ListView.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      onClick: PropTypes.func,
      icon: PropTypes.shape({ icon: PropTypes.string, type: PropTypes.string }),
    })
  ),
  filter_on_load: PropTypes.shape({
    status: PropTypes.string,
    taxonomy: PropTypes.shape({ _id: PropTypes.string, name: PropTypes.string }),
  }),
  columns: PropTypes.array,
  entities: PropTypes.string,
  onSearch: PropTypes.func,
  search: PropTypes.func,
  searchable: PropTypes.bool,
  title: PropTypes.string,
  persistentFiltersOnRefresh: PropTypes.bool,
  stateful: PropTypes.bool,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  titleClassName: PropTypes.string,
  hideLabel: PropTypes.bool,
  data: PropTypes.array,
  fields: PropTypes.array,
  queryString: PropTypes.object,
  onGetItems: PropTypes.func,
  filters: PropTypes.array,
  style: PropTypes.object,
  onSelectionChange: PropTypes.func,
  customPlaceholder: PropTypes.string,
  showCheckboxes: PropTypes.bool,
  allowSelectAllPages: PropTypes.bool,
}

ListView.defaultProps = {
  actions: [],
  columns: ['column 1', 'column 2'],
  entities: '',
  onSearch: () => {},
  search: null,
  searchable: true,
  stateful: false,
  title: '',
  persistentFiltersOnRefresh: true,
  filter_on_load: {},
  className: '',
  inputClassName: '',
  titleClassName: '',
  hideLabel: false,
  fields: [],
  queryString: {},
  onGetItems: () => {},
  filters: [],
  style: {},
  onSelectionChange: () => null,
  extractItems: (resp) => resp.items,
  extractCount: (resp) => resp.count,
  customPlaceholder: '',
  showCheckboxes: false,
  allowSelectAllPages: false,
}

// export
export default ListView
